import React, { ReactNode, useState } from 'react';
import { Box, Card, Collapse, Typography } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import {
  BatteryCabinetIcon,
  GridPoleIcon,
  HouseIcon,
  InverterIcon,
  SolarArrayIcon
} from './hubIcons';
import './dotsAnimation.css';

// Phone-only energy-flow view. Every Sodistore product is reduced to the same four
// roles, so the picture is identical across products and only the numbers differ.
// Roles that a product does not have (SodistoreGrid has no PV and no loads) are
// simply omitted — no invisible placeholder boxes.
export type RoleKey = 'pv' | 'grid' | 'battery' | 'loads';

export interface HubRole {
  key: RoleKey;
  // Watts, signed with the same convention the desktop diagram uses:
  // positive = flowing to the right in that diagram, i.e. grid importing and
  // battery charging. PV and loads are always positive.
  power: number;
  soc?: number;
  detail?: ReactNode;
}

interface MobileEnergyHubProps {
  roles: HubRole[];
  centerDetail?: ReactNode;
}

// Below this the system is effectively idle; animating it would suggest activity
// that is not there.
const ACTIVE_THRESHOLD_W = 10;

function formatPower(watts: number): string {
  const v = Math.abs(watts);
  return v >= 1000 ? `${(v / 1000).toFixed(1)} kW` : `${v.toFixed(1)} W`;
}

const ICON_SX = { fontSize: 30, display: 'block', mx: 'auto', my: 0.25 };

const ROLE_ICON: Record<RoleKey, ReactNode> = {
  pv: <SolarArrayIcon sx={{ ...ICON_SX, color: '#f7b34d' }} />,
  grid: <GridPoleIcon sx={{ ...ICON_SX, color: '#5b6b7c' }} />,
  battery: <BatteryCabinetIcon sx={{ ...ICON_SX, color: '#3fae5a' }} />,
  loads: <HouseIcon sx={{ ...ICON_SX, color: '#5b6b7c' }} />
};

const ROLE_LABEL: Record<RoleKey, { id: string; fallback: string }> = {
  pv: { id: 'hubSolar', fallback: 'Solar' },
  grid: { id: 'hubGrid', fallback: 'Grid' },
  battery: { id: 'hubBattery', fallback: 'Battery' },
  loads: { id: 'hubLoads', fallback: 'Loads' }
};

// Direction word under the value, so charge vs discharge is readable without
// having to watch the animation.
function roleStatus(role: HubRole) {
  if (Math.abs(role.power) < ACTIVE_THRESHOLD_W) return null;
  if (role.key === 'battery') {
    return role.power > 0
      ? { id: 'hubCharging', fallback: 'charging' }
      : { id: 'hubDischarging', fallback: 'discharging' };
  }
  if (role.key === 'grid') {
    return role.power > 0
      ? { id: 'hubImporting', fallback: 'importing' }
      : { id: 'hubExporting', fallback: 'exporting' };
  }
  return null;
}

interface ConnectorProps {
  orientation: 'vertical' | 'horizontal';
  power: number;
  // Which way a positive value travels, in screen terms. Both roles that can go
  // negative (grid export, battery discharge) are placed so positive means down/right.
  positiveGoes: 'down' | 'right';
  length?: number;
}

function Connector({ orientation, power, positiveGoes, length }: ConnectorProps) {
  const active = Math.abs(power) >= ACTIVE_THRESHOLD_W;
  // The gradient always marches down/right; a flow going the other way just runs
  // the same animation in reverse.
  const forward = power > 0;
  const classes = [
    'hubFlow',
    orientation,
    active ? 'active' : '',
    active && !forward ? 'reverse' : ''
  ]
    .filter(Boolean)
    .join(' ');

  const size = length ?? 22;
  return (
    <Box
      className={classes}
      sx={
        orientation === 'vertical'
          ? { height: `${size}px`, mx: 'auto' }
          : { flex: `0 0 ${size}px`, alignSelf: 'center' }
      }
    />
  );
}

// 'row' boxes share the middle row with two connectors and must be able to shrink;
// 'stack' boxes sit alone in a centred column and keep a fixed width.
const BOX_SIZING = {
  row: { flex: '1 1 0', minWidth: 0, maxWidth: 104 },
  stack: { width: 104 }
};

function HubBox({
  role,
  expanded,
  onToggle,
  sizing = 'row'
}: {
  role: HubRole;
  expanded: boolean;
  onToggle: () => void;
  sizing?: 'row' | 'stack';
}) {
  const label = ROLE_LABEL[role.key];
  const status = roleStatus(role);
  const tappable = !!role.detail;

  return (
    <Card
      onClick={tappable ? onToggle : undefined}
      sx={{
        ...BOX_SIZING[sizing],
        px: 0.5,
        py: 1,
        textAlign: 'center',
        borderRadius: 2,
        boxShadow: expanded ? 4 : 1,
        cursor: tappable ? 'pointer' : 'default',
        border: expanded ? '1px solid #f7b34d' : '1px solid transparent'
      }}
    >
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#5b6b7c' }}>
        <FormattedMessage id={label.id} defaultMessage={label.fallback} />
      </Typography>
      {ROLE_ICON[role.key]}
      {role.soc !== undefined && (
        <>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700 }}>
            {role.soc.toFixed(0)} %
          </Typography>
          <Box sx={{ height: 4, borderRadius: 2, bgcolor: '#e6e6e6', mx: 0.5 }}>
            <Box
              sx={{
                height: 4,
                borderRadius: 2,
                bgcolor: '#3fae5a',
                width: `${Math.max(0, Math.min(100, role.soc))}%`
              }}
            />
          </Box>
        </>
      )}
      <Typography sx={{ fontSize: '0.8rem', fontWeight: 600 }}>
        {formatPower(role.power)}
      </Typography>
      {status && (
        <Typography sx={{ fontSize: '0.6rem', color: '#8a97a3' }}>
          <FormattedMessage id={status.id} defaultMessage={status.fallback} />
        </Typography>
      )}
    </Card>
  );
}

function MobileEnergyHub({ roles, centerDetail }: MobileEnergyHubProps) {
  const [expanded, setExpanded] = useState<RoleKey | 'center' | null>(null);
  const find = (k: RoleKey) => roles.find((r) => r.key === k);
  const pv = find('pv');
  const grid = find('grid');
  const battery = find('battery');
  const loads = find('loads');

  const toggle = (k: RoleKey | 'center') =>
    setExpanded((cur) => (cur === k ? null : k));

  const openDetail =
    expanded === 'center' ? centerDetail : find(expanded as RoleKey)?.detail;

  return (
    <Box sx={{ width: '100%', py: 1 }}>
      {pv && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <HubBox
            role={pv}
            expanded={expanded === 'pv'}
            onToggle={() => toggle('pv')}
            sizing="stack"
          />
          <Connector orientation="vertical" power={pv.power} positiveGoes="down" />
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {grid && (
          <>
            <HubBox
              role={grid}
              expanded={expanded === 'grid'}
              onToggle={() => toggle('grid')}
            />
            <Connector orientation="horizontal" power={grid.power} positiveGoes="right" />
          </>
        )}
        <Card
          onClick={centerDetail ? () => toggle('center') : undefined}
          sx={{
            ...BOX_SIZING.row,
            py: 1.2,
            textAlign: 'center',
            borderRadius: 2,
            boxShadow: expanded === 'center' ? 4 : 1,
            cursor: centerDetail ? 'pointer' : 'default',
            border: expanded === 'center' ? '1px solid #f7b34d' : '1px solid transparent'
          }}
        >
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#5b6b7c' }}>
            sodistore
          </Typography>
          <InverterIcon sx={{ ...ICON_SX, color: '#f7b34d' }} />
        </Card>
        {battery && (
          <>
            <Connector
              orientation="horizontal"
              power={battery.power}
              positiveGoes="right"
            />
            <HubBox
              role={battery}
              expanded={expanded === 'battery'}
              onToggle={() => toggle('battery')}
            />
          </>
        )}
      </Box>

      {loads && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Connector orientation="vertical" power={loads.power} positiveGoes="down" />
          <HubBox
            role={loads}
            expanded={expanded === 'loads'}
            onToggle={() => toggle('loads')}
            sizing="stack"
          />
        </Box>
      )}

      <Collapse in={!!openDetail} unmountOnExit>
        <Card sx={{ mt: 1.5, p: 1.5, borderRadius: 2 }}>{openDetail}</Card>
      </Collapse>
    </Box>
  );
}

export default MobileEnergyHub;
